import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Send, CheckCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import {
  fetchFormFields,
  submitFormToCRM,
  CRM_FORM_ID,
  type FormField,
} from "@/lib/crm-client";

interface LeadFormProps {
  title?: string;
  description?: string;
}

export function LeadForm({
  title = "Agende uma demonstração",
  description = "Preencha o formulário e nossa equipe entrará em contato em até 24h.",
}: LeadFormProps) {
  const [fields, setFields] = useState<FormField[]>([]);
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [consent, setConsent] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    fetchFormFields(CRM_FORM_ID)
      .then((data) => {
        setFields(data);
        const initial: Record<string, string> = {};
        data.forEach((f) => (initial[f.label] = ""));
        setFormData(initial);
      })
      .catch((err) => {
        console.error("Error loading form fields:", err);
        setLoadError(true);
      })
      .finally(() => setIsLoading(false));
  }, []);

  const handleChange = (label: string, value: string) => {
    setFormData((prev) => ({ ...prev, [label]: value }));
  };

  const consentText =
    "Ao enviar este formulário, concordo com a Política de Privacidade e autorizo o contato por parte da Cogni.";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setIsSubmitting(true);
    try {
      await submitFormToCRM(CRM_FORM_ID, formData, consentText);

      // Deduplication event ID for Meta Pixel + CAPI
      const eventId = `lead_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

      // Meta Pixel — client-side Lead event
      if (typeof fbq === "function") {
        fbq("track", "Lead", {}, { eventID: eventId });
      }

      // Google Ads conversion
      if (typeof gtag === "function") {
        gtag("event", "conversion", {
          send_to: "AW-18054925757",
        });
      }

      // Meta CAPI — server-side Lead event (fire-and-forget)
      try {
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
        if (supabaseUrl) {
          fetch(`${supabaseUrl}/functions/v1/meta-capi`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              event_name: "Lead",
              event_id: eventId,
              event_source_url: window.location.href,
              user_data: {
                email: formData["E-mail"] || formData["Email"] || "",
                phone: formData["Telefone"] || formData["WhatsApp"] || "",
                fn: formData["Nome"] || "",
              },
            }),
          }).catch(() => {});
        }
      } catch {}

      navigate("/obrigado");
    } catch (err) {
      console.error("Error submitting form:", err);
      toast({
        title: "Erro ao enviar",
        description: "Tente novamente em alguns instantes.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <Card className="border-border">
        <CardContent className="p-6 sm:p-8 text-center">
          <CheckCircle className="h-12 w-12 sm:h-16 sm:w-16 text-green-500 mx-auto mb-4" />
          <h3 className="text-xl sm:text-2xl font-bold mb-2">Mensagem enviada!</h3>
          <p className="text-muted-foreground text-sm sm:text-base">
            Nossa equipe entrará em contato em breve.
          </p>
        </CardContent>
      </Card>
    );
  }

  if (isLoading) {
    return (
      <Card className="border-border">
        <CardContent className="p-6 sm:p-8 flex items-center justify-center min-h-[200px]">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (loadError) {
    return (
      <Card className="border-border">
        <CardContent className="p-6 sm:p-8 text-center">
          <p className="text-muted-foreground text-sm">
            Não foi possível carregar o formulário. Tente novamente mais tarde.
          </p>
        </CardContent>
      </Card>
    );
  }

  const renderField = (field: FormField) => {
    const { label, type, required, options, placeholder } = field;
    const id = `field-${field.id}`;

    if (type === "select" && options && options.length > 0) {
      return (
        <div key={field.id} className="space-y-2">
          <Label htmlFor={id}>
            {label} {required && "*"}
          </Label>
          <Select
            value={formData[label] || ""}
            onValueChange={(v) => handleChange(label, v)}
            required={required}
          >
            <SelectTrigger className="touch-target">
              <SelectValue placeholder={placeholder || `Selecione ${label.toLowerCase()}`} />
            </SelectTrigger>
            <SelectContent>
              {options.map((opt) => (
                <SelectItem key={opt} value={opt}>
                  {opt}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      );
    }

    if (type === "textarea") {
      return (
        <div key={field.id} className="space-y-2">
          <Label htmlFor={id}>
            {label} {required && "*"}
          </Label>
          <Textarea
            id={id}
            placeholder={placeholder || ""}
            required={required}
            rows={4}
            value={formData[label] || ""}
            onChange={(e) => handleChange(label, e.target.value)}
          />
        </div>
      );
    }

    // text, email, phone, number, date, url — all map to <Input>
    const inputType =
      type === "phone"
        ? "tel"
        : type === "email"
        ? "email"
        : type === "number"
        ? "number"
        : type === "date"
        ? "date"
        : type === "url"
        ? "url"
        : "text";

    const isPhone = type === "phone";

    const formatPhone = (value: string) => {
      const digits = value.replace(/\D/g, "").slice(0, 11);
      if (digits.length <= 2) return digits.length ? `(${digits}` : "";
      if (digits.length <= 6) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
      if (digits.length <= 10)
        return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
      return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
    };

    return (
      <div key={field.id} className="space-y-2">
        <Label htmlFor={id}>
          {label} {required && "*"}
        </Label>
        <Input
          id={id}
          type={inputType}
          placeholder={isPhone ? "(00) 00000-0000" : placeholder || ""}
          required={required}
          className="touch-target"
          maxLength={isPhone ? 15 : undefined}
          value={formData[label] || ""}
          onChange={(e) =>
            handleChange(label, isPhone ? formatPhone(e.target.value) : e.target.value)
          }
        />
      </div>
    );
  };

  return (
    <Card className="border-border">
      <CardHeader className="px-4 sm:px-6">
        <CardTitle className="text-xl sm:text-2xl">{title}</CardTitle>
        <CardDescription className="text-sm">{description}</CardDescription>
      </CardHeader>
      <CardContent className="px-4 sm:px-6">
        <form onSubmit={handleSubmit} className="space-y-4">
          {fields.map(renderField)}


          <Button
            type="submit"
            className="w-full gradient-primary text-primary-foreground touch-target"
            size="lg"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
            ) : (
              <Send className="mr-2 h-5 w-5" />
            )}
            {isSubmitting ? "Enviando..." : title === "Quero ver a Cogni na minha operação" ? "Quero ver a Cogni na minha operação" : "Enviar solicitação"}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
